/**
 * Textos y tipos compartidos: certificados de descarga ↔ cadena de custodia ↔ casos SPOA en Grabaciones.
 * La exportación firmada (ZIP/PDF + hash) puede listarse en backend cuando el despliegue registra esas descargas (API de listado).
 */

export interface CustodyCertificateRow {
  id: string;
  spoa: string;
  siteName: string;
  address: string;
  investigatorName: string;
  /** ISO 8601 */
  createdAt: string;
  /** Id del registro de descarga en backend, si aplica al despliegue */
  recordDownloadId?: number | null;
  /** Vista previa del hash de integridad del paquete (hex truncado) */
  hashPreview?: string | null;
  /** Metadatos de reproducción para abrir Grabaciones en el mismo segmento */
  siteId?: number | null;
  cameraIdNum?: number | null;
  /** Inicio del clip exportado, ISO 8601 */
  segmentStart?: string | null;
  /** Fin del clip exportado, ISO 8601 */
  segmentEnd?: string | null;
}

/**
 * Ruta a Grabaciones con deep link compatible con `RecordingsPage` (`cert=1`, mismo patrón que analíticas).
 * Devuelve null si faltan sitio, cámara o instante inicial.
 */
/** Resumen de lo registrado en Grabaciones (sessionStorage) para un número SPOA. */
export interface SpoaSessionAggregate {
  evidenceEntries: number;
  photoCount: number;
  /** Días (YYYY-MM-DD) en los que hay expediente con ese SPOA */
  contextDates: string[];
  lastUpdated: number;
}

/**
 * Une todos los casos guardados en el navegador que coinciden con el número SPOA del certificado.
 */
export function aggregateSpoaSessionForNumber(
  spoa: string,
  docs: Array<{
    spoa: string;
    updatedAt: number;
    evidences: Array<{ items: Array<unknown> }>;
    contextDate?: string;
  }>
): SpoaSessionAggregate | null {
  const norm = spoa.trim().toLowerCase();
  const matches = docs.filter((d) => d.spoa.trim().toLowerCase() === norm);
  if (matches.length === 0) return null;
  let evidenceEntries = 0;
  let photoCount = 0;
  let lastUpdated = 0;
  const dates = new Set<string>();
  for (const d of matches) {
    lastUpdated = Math.max(lastUpdated, d.updatedAt);
    if (d.contextDate) dates.add(d.contextDate);
    for (const ev of d.evidences) {
      evidenceEntries += 1;
      photoCount += Array.isArray(ev.items) ? ev.items.length : 0;
    }
  }
  return {
    evidenceEntries,
    photoCount,
    contextDates: Array.from(dates).sort(),
    lastUpdated,
  };
}

export function buildRecordingsCertPreviewPath(
  row: Pick<CustodyCertificateRow, 'siteId' | 'cameraIdNum' | 'segmentStart' | 'segmentEnd'>
): string | null {
  if (row.siteId == null || row.cameraIdNum == null || !row.segmentStart) return null;
  const t = new Date(row.segmentStart);
  if (Number.isNaN(t.getTime())) return null;
  let playbackMin = 5;
  if (row.segmentEnd) {
    const end = new Date(row.segmentEnd);
    if (!Number.isNaN(end.getTime()) && end > t) {
      const m = Math.ceil((end.getTime() - t.getTime()) / 60000);
      if (m >= 2 && m <= 20) playbackMin = m;
      else if (m > 20) playbackMin = 20;
      else if (m === 1) playbackMin = 2;
    }
  }
  const params = new URLSearchParams({
    cert: '1',
    time: t.toISOString(),
    cameraId: String(row.cameraIdNum),
    siteId: String(row.siteId),
    playbackMin: String(playbackMin),
  });
  return `/recordings?${params.toString()}`;
}

export const CUSTODY_CHAIN_TITLE = 'Cadena de custodia';

export const CUSTODY_CHAIN_EXPLAINER =
  'La cadena de custodia documenta quién accede a las pruebas digitales (exportaciones de grabación, evidencias fotográficas asociadas a un SPOA) y garantiza su integridad mediante registro y huellas SHA-256 y MD5 verificables en constancia y certificado de custodia.';

export const CUSTODY_GRABACIONES_HINT =
  'En Grabaciones puedes documentar casos SPOA, evidencias con tipo de delito y capturas del día; esos datos complementan el expediente vinculado al certificado de descarga.';

export function formatCertificateDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-CO', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function custodyStepsShort(): string[] {
  return [
    'Solicitud o programación de exportación con contexto SPOA / sitio / cámara.',
    'Generación del paquete (vídeo y/o certificado PDF) y cálculo de huellas SHA-256 y MD5 (solicitud JSON + binario MP4).',
    'Registro en base de datos con metadatos y usuario responsable, si el despliegue mantiene ese historial.',
    'Entrega o descarga auditada; cualquier nueva evidencia en Grabaciones queda referenciada al mismo expediente SPOA.',
  ];
}
